import { FormControl, Stack, TextField, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { PasswordTextField } from 'components/common/PasswordTextField'
import { type AuthenticationFormValues } from 'components/status-checks/authentication-form-utils'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { TeamExternalIntegrationAuthenticationStatus } from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const AWSServiceAccountAuthentication: React.FC = () => {
  const { isEdit, isDrawerFlow } = useObservabilityToolAuthenticationContext()

  const form = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()
  const { control, setValue } = form

  const externalIntegration = useWatch({
    control,
    name: 'externalIntegration'
  })

  // Puts values into state if we are editing an existing AWS authentication
  useEffect(() => {
    if (
      isEdit &&
      externalIntegration?.isConfigured &&
      externalIntegration?.lastAuthenticationStatus ===
        TeamExternalIntegrationAuthenticationStatus.Authenticated
    ) {
      setValue('domain', {
        name: externalIntegration?.domain ?? '',
        integrationType: IntegrationType.AWS
      })
      setValue(
        'awsAccessKeyID',
        externalIntegration.integrationSpecificValues?.AWS_ACCESS_KEY_ID || ''
      )
      setValue(
        'awsSecretAccessKey',
        externalIntegration.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ||
          ''
      )
    }
  }, [
    externalIntegration?.domain,
    externalIntegration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    externalIntegration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY,
    externalIntegration?.isConfigured,
    externalIntegration?.lastAuthenticationStatus,
    isEdit,
    setValue
  ])

  return (
    <Stack ml={2} spacing={3}>
      {!isEdit && (
        <FormControl>
          <Typography mb={2}>What AWS Account ID will be used?</Typography>
          <Controller
            control={control}
            name="domain.name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                data-testid="aws-account-id-input"
                disabled={isEdit}
                error={Boolean(error)}
                helperText={error?.message}
                label="AWS Account ID"
                variant="outlined"
              />
            )}
          />
        </FormControl>
      )}
      <FormControl>
        <Typography mb={2}>What are the AWS credentials?</Typography>
        <Stack
          direction={isDrawerFlow ? 'column' : 'row'}
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Controller
            control={control}
            name="awsAccessKeyID"
            render={({ field, fieldState: { error } }) => (
              <PasswordTextField
                {...field}
                autoComplete="off"
                data-testid="aws-access-key-id-input"
                error={Boolean(error)}
                helperText={error?.message}
                label="AWS Access Key ID"
                variant="outlined"
              />
            )}
          />
          <Controller
            control={control}
            name="awsSecretAccessKey"
            render={({ field, fieldState: { error } }) => (
              <PasswordTextField
                {...field}
                autoComplete="off"
                data-testid="aws-secret-access-key-input"
                error={Boolean(error)}
                helperText={error?.message}
                label="AWS Secret Access Key"
                variant="outlined"
              />
            )}
          />
        </Stack>
      </FormControl>
    </Stack>
  )
}
